import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { minisiteGet, minisitePost } from '../utils/minisiteApi';
import { useT } from '../i18n';

// Réponses des invités, visibles uniquement avec le lien d'édition (jeton vérifié côté serveur).
const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const MinisiteRsvpResponses = ({ slug, token, onCount }) => {
  const t = useT('rsvp-responses');
  const [responses, setResponses] = useState(null);

  const load = useCallback(() => {
    minisiteGet('getMinisiteRsvps', { slug, token }).then((data) => {
      const list = data?.success ? data.data : [];
      setResponses(list);
      onCount?.(list.length);
    });
  }, [slug, token, onCount]);

  useEffect(() => { load(); }, [load]);

  if (!responses) return <Typography>{t('rsvp-responses.loading')}</Typography>;

  const presents = responses.filter((r) => Number(r.presence) === 1);
  const totalPersonnes = presents.reduce((sum, r) => sum + Number(r.nb_personnes || 1) + Number(r.nb_enfants || 0), 0);
  const totalEnfants = presents.reduce((sum, r) => sum + Number(r.nb_enfants || 0), 0);
  // Réponses aux questions libres : « question : réponse », une par ligne.
  const libres = (r) => {
    try {
      return (JSON.parse(r.reponses || '[]') || []).map((x) => t('rsvp-responses.question', { question: x.question ?? '', answer: x.reponse ?? '' })).join('\n');
    } catch (e) {
      return '';
    }
  };

  const handleDelete = (id) => {
    if (!window.confirm(t('rsvp-responses.confirmDelete'))) return;
    minisitePost('deleteMinisiteRsvp', { slug, token, rsvp_id: id }).then(load);
  };

  // Séparateur « ; » et BOM UTF-8 : s'ouvre directement dans Excel en français. Les en-têtes, Oui/Non et le nom
  // du fichier sont dans la langue de l'acheteur ; ce que les invités ont écrit n'est jamais traduit.
  const handleExport = () => {
    const header = [
      t('rsvp-responses.columns.name'), t('rsvp-responses.columns.presence'), t('rsvp-responses.csv.adults'), t('rsvp-responses.csv.children'),
      t('rsvp-responses.columns.meals'), t('rsvp-responses.columns.allergies'), t('rsvp-responses.columns.other'),
      t('rsvp-responses.columns.message'), t('rsvp-responses.csv.date'),
    ];
    const rows = responses.map((r) => [
      r.nom, Number(r.presence) === 1 ? t('rsvp-responses.yes') : t('rsvp-responses.no'), r.nb_personnes, r.nb_enfants, r.repas, r.allergies, libres(r), r.message, r.created_at,
    ]);
    const csv = [header, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = t('rsvp-responses.csv.file');
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', marginBottom: 2 }}>
        <Typography>
          {t.rich('rsvp-responses.summary.people', { count: totalPersonnes })}
          {totalEnfants > 0 ? ` ${t('rsvp-responses.summary.children', { count: totalEnfants })}` : ''}
          {' · '}{t('rsvp-responses.summary.yes', { n: presents.length })} · {t('rsvp-responses.summary.no', { n: responses.length - presents.length })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={load}>{t('rsvp-responses.refresh')}</Button>
          <Button size="small" variant="outlined" onClick={handleExport} disabled={responses.length === 0}>
            {t('rsvp-responses.export')}
          </Button>
        </Box>
      </Box>

      {responses.length === 0 ? (
        <Typography color="text.secondary">{t('rsvp-responses.empty')}</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('rsvp-responses.columns.name')}</TableCell>
                <TableCell>{t('rsvp-responses.columns.presence')}</TableCell>
                <TableCell>{t('rsvp-responses.columns.people')}</TableCell>
                <TableCell>{t('rsvp-responses.columns.meals')}</TableCell>
                <TableCell>{t('rsvp-responses.columns.allergies')}</TableCell>
                <TableCell>{t('rsvp-responses.columns.other')}</TableCell>
                <TableCell>{t('rsvp-responses.columns.message')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {responses.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.nom}</TableCell>
                  <TableCell>{Number(r.presence) === 1 ? t('rsvp-responses.yes') : t('rsvp-responses.no')}</TableCell>
                  <TableCell>
                    {Number(r.presence) === 1 ? (Number(r.nb_enfants) > 0 ? t('rsvp-responses.withChildren', { adults: r.nb_personnes, children: r.nb_enfants }) : `${r.nb_personnes}`) : '—'}
                  </TableCell>
                  <TableCell>{r.repas}</TableCell>
                  <TableCell>{r.allergies}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-line' }}>{libres(r)}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'pre-line' }}>{r.message}</TableCell>
                  <TableCell>
                    <IconButton size="small" aria-label={t('rsvp-responses.delete')} onClick={() => handleDelete(r.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default MinisiteRsvpResponses;
